import { useCallback, useEffect, useState } from 'react';
import type { FilterRule } from '@/lib/tableFilters/filterRuleTypes';
import type { FilterPreset } from '@/utils/filterPresetsStorage';
import {
  FILTER_PRESETS_STORAGE_KEY,
  loadFilterPresets,
  saveFilterPresets,
} from '@/utils/filterPresetsStorage';

function newPresetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function useFilterPresets(storageKey: string = FILTER_PRESETS_STORAGE_KEY) {
  const [presets, setPresets] = useState<FilterPreset[]>(() => loadFilterPresets(storageKey));

  useEffect(() => {
    setPresets(loadFilterPresets(storageKey));
  }, [storageKey]);

  useEffect(() => {
    saveFilterPresets(presets, storageKey);
  }, [presets, storageKey]);

  const addPreset = useCallback((name: string, filters: Record<string, FilterRule>) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const entry: FilterPreset = {
      id: newPresetId(),
      name: trimmed,
      filters: { ...filters },
      createdAt: Date.now(),
    };
    setPresets((prev) => [...prev, entry]);
  }, []);

  const removePreset = useCallback((id: string) => {
    setPresets((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return { presets, addPreset, removePreset };
}
